import React from 'react';
import Select from 'react-select';
import { Link } from 'react-router-dom';
import { PERIODO_PADRAO, PERIODO_PERSONALIZADO, REGIOES, UFS } from 'services/price_intelligence';
import { Container } from './styles';

export const PERIODOS = [
  { label: '3 meses', meses: 3 },
  { label: '6 meses', meses: 6 },
  { label: '12 meses', meses: 12 },
  { label: '18 meses', meses: 18 },
  { label: '24 meses', meses: 24 },
];

const SUGESTOES = [
  'Notebook i5 16GB SSD',
  'Papel A4 75g resma',
  'Pneu 175/65 R14',
  'Ar condicionado split 12.000 BTUs',
  'Cadeira giratória com braços',
];

interface UfOption {
  value: string;
  label: string;
}

const UF_OPTIONS: UfOption[] = Object.keys(REGIOES)
  .map((regiao) => ({ value: `regiao:${regiao}`, label: `Região ${regiao}` }))
  .concat(UFS.map((uf) => ({ value: uf, label: uf })));

interface PriceIntelligenceSearchProps {
  query: string;
  onChangeQuery: (valor: string) => void;
  periodoMeses: number;
  onChangePeriodo: (meses: number) => void;
  ufs: string[];
  onChangeUfs: (ufs: string[]) => void;
  dataDe: string;
  dataAte: string;
  onChangeDatas: (de: string, ate: string) => void;
  onSearch: (query?: string) => void;
  onImportar: () => void;
  buscando: boolean;
  loteRodando: boolean;
  totalItens: number | null;
  mostrarSugestoes: boolean;
}

const PriceIntelligenceSearch = ({
  query,
  onChangeQuery,
  periodoMeses,
  onChangePeriodo,
  ufs,
  onChangeUfs,
  dataDe,
  dataAte,
  onChangeDatas,
  onSearch,
  onImportar,
  buscando,
  loteRodando,
  totalItens,
  mostrarSugestoes,
}: PriceIntelligenceSearchProps) => {
  const travado = buscando || loteRodando;
  const personalizado = periodoMeses === PERIODO_PERSONALIZADO;

  const baseLabel =
    totalItens && totalItens > 1000000
      ? `${(totalItens / 1000000).toLocaleString('pt-BR', {
          maximumFractionDigits: 1,
        })} milhões de itens homologados`
      : 'milhões de itens homologados';

  const selectedOptions: UfOption[] = ufs.map((uf) => ({ value: uf, label: uf }));

  const handleUfsChange = (selected: any) => {
    const values: string[] = (selected || []).map((opt: UfOption) => opt.value);
    const expanded: string[] = [];
    values.forEach((value) => {
      if (value.indexOf('regiao:') === 0) {
        const regiao = value.replace('regiao:', '');
        (REGIOES[regiao] || []).forEach((uf) => {
          if (expanded.indexOf(uf) < 0) expanded.push(uf);
        });
      } else if (expanded.indexOf(value) < 0) {
        expanded.push(value);
      }
    });
    onChangeUfs(expanded);
  };

  const limparFiltros = () => {
    onChangeUfs([]);
    onChangeDatas('', '');
    onChangePeriodo(PERIODO_PADRAO);
  };

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-start flex-wrap">
        <div>
          <div className="page-head">
            <h4>Análise de Mercado 3.0</h4>
            <span className="ia-pill">
              <i className="material-icons">auto_awesome</i>
              IA
            </span>
          </div>
          <p className="page-sub">
            Saiba quem compra, quem vence e quais preços são praticados nas compras públicas.
            Antecipe oportunidades com as contratações projetadas pela Administração Pública e
            prepare propostas mais competitivas.
          </p>
        </div>
        <div className="head-actions">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={onImportar}
            disabled={travado}
            title="Cote vários itens de uma vez a partir da planilha, do PDF ou do Word do edital"
          >
            <i className="material-icons align-middle mr-1" style={{ fontSize: 16 }}>
              upload_file
            </i>
            Cotar itens de um arquivo
          </button>
          <Link to="/pesquisa_de_precos/salvas" className="btn btn-outline-secondary btn-sm">
            <i className="material-icons align-middle mr-1" style={{ fontSize: 16 }}>
              bookmark_border
            </i>
            Pesquisas salvas
          </Link>
        </div>
      </div>

      <div className="trust-chips">
        <span className="chip">
          <i className="material-icons">hub</i>
          11 portais integrados
        </span>
        <span className="chip">
          <i className="material-icons">inventory_2</i>
          {baseLabel}
        </span>
        <span className="chip">
          <i className="material-icons">auto_awesome</i>
          Curadoria de IA item a item
        </span>
      </div>

      <div className="search-card">
        <form
          className="search-row"
          onSubmit={(e) => {
            e.preventDefault();
            if (!travado && query.trim()) onSearch();
          }}
        >
          <div className="search-input-wrap">
            <i className="material-icons">search</i>
            <input
              type="text"
              className="form-control"
              value={query}
              onChange={(e) => onChangeQuery(e.target.value)}
              placeholder="O que você vai cotar? Ex.: notebook i5 16GB SSD · pneu 175/65 R14"
              aria-label="Item a pesquisar"
              disabled={travado}
            />
            {query && !travado && (
              <button
                type="button"
                className="clear-btn"
                onClick={() => onChangeQuery('')}
                aria-label="Limpar busca"
              >
                <i className="material-icons">close</i>
              </button>
            )}
          </div>
          <button
            type="submit"
            className="btn btn-primary btn-ia"
            disabled={travado || !query.trim()}
            title={loteRodando ? 'Aguarde a pesquisa dos itens do arquivo terminar' : undefined}
          >
            {buscando ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            ) : (
              <i className="material-icons">search</i>
            )}
            {buscando ? 'Pesquisando…' : 'Pesquisar'}
          </button>
        </form>

        <div className="filters-row">
          <span className="filter-label">
            <i className="material-icons">calendar_today</i>
            Período
          </span>
          <div className="period-group" role="group" aria-label="Período da pesquisa">
            {PERIODOS.map((p) => (
              <button
                key={p.meses}
                type="button"
                className={periodoMeses === p.meses ? 'active' : ''}
                onClick={() => onChangePeriodo(p.meses)}
                disabled={travado}
              >
                {p.label}
              </button>
            ))}
            <button
              type="button"
              className={personalizado ? 'active' : ''}
              onClick={() => onChangePeriodo(PERIODO_PERSONALIZADO)}
              disabled={travado}
            >
              Personalizado
            </button>
          </div>
          {personalizado && (
            <div className="custom-dates">
              <input
                type="date"
                className="form-control form-control-sm"
                value={dataDe}
                max={dataAte || undefined}
                onChange={(e) => onChangeDatas(e.target.value, dataAte)}
                disabled={travado}
                aria-label="Data inicial"
              />
              <span>até</span>
              <input
                type="date"
                className="form-control form-control-sm"
                value={dataAte}
                min={dataDe || undefined}
                onChange={(e) => onChangeDatas(dataDe, e.target.value)}
                disabled={travado}
                aria-label="Data final"
              />
            </div>
          )}

          <span className="filter-label">
            <i className="material-icons">place</i>
            Abrangência
          </span>
          <div className="uf-select">
            <Select
              classNamePrefix="pi-select"
              isMulti
              options={UF_OPTIONS}
              value={selectedOptions}
              onChange={handleUfsChange}
              placeholder="Brasil inteiro"
              noOptionsMessage={() => 'Nenhuma opção'}
              isDisabled={travado}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 100000 }),
              }}
            />
          </div>

          {(ufs.length > 0 || periodoMeses !== PERIODO_PADRAO) && (
            <button
              type="button"
              className="clear-filters"
              onClick={limparFiltros}
              disabled={travado}
            >
              <i className="material-icons">close</i>
              Limpar filtros
            </button>
          )}
        </div>
      </div>

      {mostrarSugestoes && (
        <div className="suggestions">
          <span className="suggestions-label">Experimente:</span>
          {SUGESTOES.map((sugestao) => (
            <button
              key={sugestao}
              type="button"
              onClick={() => onSearch(sugestao)}
              disabled={travado}
            >
              {sugestao}
            </button>
          ))}
        </div>
      )}
    </Container>
  );
};

export default PriceIntelligenceSearch;
